// <s-product-gallery>: one scroll-snapped main stage plus a thumbnail row.
// Thumbnails and arrows use invoker commands (--go, --prev, --next); swipe is native scroll-snap.
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    if (!this.track || this.slides.length === 0) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--go') {
        this.go(Number(event.source?.dataset.index || 0));
      } else if (event.command === '--prev') {
        this.go(this.#index - 1);
      } else if (event.command === '--next') {
        this.go(this.#index + 1);
      }
    }, opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#setActive(this.slides.indexOf(entry.target));
        }
      }
    }, { root: this.track, threshold: 0.6 });

    for (const slide of this.slides) this.#observer.observe(slide);
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  go(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slides[target].inert = false;
    this.slides[target].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    this.#setActive(target);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
